// tslint:disable
/**
 * TC API
 * TestCollab is a test management tool that helps development teams carry out testing effectively and avoid failures. # Introduction This is an API documentation for TestCollab 2.   # Authentication TestCollab supports two forms of authentication:   - An <b>API token</b> is required to make API calls. The token can be generated from user's profile; to obtain it login to your TestCollab account and go to ['My Profile Settings'](https://testcollab.io/my_profile) and switch to API Token tab. Click on \"Generate new API token\", Once the token is generated it can be sent as a query parameter for the API calls made - https://api.testcollab.io/projects?token=<generated-API-token>    - <b>Bearer authentication</b> (also called token authentication) is an HTTP authentication scheme that involves security tokens called bearer tokens. You can generate bearer auth token using [Login](#operation/login) API.     Send this token as the Authorization header while making requests to protected resources:     `Authorization: Bearer <token>`    For making API calls, we recommend using the <b>API token</b> generated from user profile page as it won't affect the on-going user session and will be available until it is revoked by the user who generated it.      # Error Handling Error responses   - `Not Found` - Error is sent when requested resource is not available.   - `Unauthorized - invalid token` - Error is sent when API token sent with the request is invalid or revoked. Error contains status code 401        - `Forbidden` - Error is sent when logged in user is not part of the company, project or his role does not allow to complete given action. Response consist of 403 status code and more information is given in error message.
 *
 * OpenAPI spec version: 2.3.1
 * Contact: support@testcollab.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import * as runtime from "../runtime";
import {
  DashboardWidget,
  DashboardWidgetFromJSON,
  DashboardWidgetToJSON,
  DashboardWidgetLayoutPayload,
  DashboardWidgetLayoutPayloadFromJSON,
  DashboardWidgetLayoutPayloadToJSON,
  DashboardWidgetPayload,
  DashboardWidgetPayloadFromJSON,
  DashboardWidgetPayloadToJSON,
  DashboardWidgetSortOrderPayload,
  DashboardWidgetSortOrderPayloadFromJSON,
  DashboardWidgetSortOrderPayloadToJSON,
  TCError,
  TCErrorFromJSON,
  TCErrorToJSON,
} from "../models";

export interface CreateDashboardWidgetRequest {
  dashboardWidgetPayload?: DashboardWidgetPayload;
}

export interface DeleteDashboardWidgetRequest {
  id: number;
}

export interface GetDashboardWidgetsRequest {
  project: number;
  sort?: string;
}

export interface SetDashboardWidgetLayoutRequest {
  dashboardWidgetLayoutPayload?: DashboardWidgetLayoutPayload;
}

export interface SetDashboardWidgetSortOrderRequest {
  dashboardWidgetSortOrderPayload?: DashboardWidgetSortOrderPayload;
}

/**
 * no description
 */
export class DashboardWidgetsApi extends runtime.BaseAPI {
  /**
   * Pin a saved report template as a dashboard widget
   * Pin a report template to the dashboard
   */
  async createDashboardWidgetRaw(
    requestParameters: CreateDashboardWidgetRequest
  ): Promise<runtime.ApiResponse<DashboardWidget>> {
    const queryParameters: runtime.HTTPQuery = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters["Content-Type"] = "application/json";

    if (this.configuration && this.configuration.apiKey) {
      headerParameters["Authorization"] =
        this.configuration.apiKey("Authorization"); // bearerAuth authentication
    }

    const response = await this.request({
      path: `/dashboardwidgets`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: DashboardWidgetPayloadToJSON(
        requestParameters.dashboardWidgetPayload
      ),
    });

    return new runtime.JSONApiResponse(response, (jsonValue) =>
      DashboardWidgetFromJSON(jsonValue)
    );
  }

  /**
   * Pin a saved report template as a dashboard widget
   * Pin a report template to the dashboard
   */
  async createDashboardWidget(
    requestParameters: CreateDashboardWidgetRequest
  ): Promise<DashboardWidget> {
    const response = await this.createDashboardWidgetRaw(requestParameters);
    return await response.value();
  }

  /**
   * Remove a pinned report template from the dashboard
   * Unpin a dashboard widget
   */
  async deleteDashboardWidgetRaw(
    requestParameters: DeleteDashboardWidgetRequest
  ): Promise<runtime.ApiResponse<any>> {
    if (requestParameters.id === null || requestParameters.id === undefined) {
      throw new runtime.RequiredError(
        "id",
        "Required parameter requestParameters.id was null or undefined when calling deleteDashboardWidget."
      );
    }

    const queryParameters: runtime.HTTPQuery = {};

    const headerParameters: runtime.HTTPHeaders = {};

    if (this.configuration && this.configuration.apiKey) {
      headerParameters["Authorization"] =
        this.configuration.apiKey("Authorization"); // bearerAuth authentication
    }

    const response = await this.request({
      path: `/dashboardwidgets/{id}`.replace(
        `{${"id"}}`,
        encodeURIComponent(String(requestParameters.id))
      ),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters,
    });

    return new runtime.TextApiResponse(response);
  }

  /**
   * Remove a pinned report template from the dashboard
   * Unpin a dashboard widget
   */
  async deleteDashboardWidget(
    requestParameters: DeleteDashboardWidgetRequest
  ): Promise<any> {
    const response = await this.deleteDashboardWidgetRaw(requestParameters);
    return await response.value();
  }

  /**
   * Get list of report templates pinned to a project's dashboard
   * Get list of pinned dashboard widgets
   */
  async getDashboardWidgetsRaw(
    requestParameters: GetDashboardWidgetsRequest
  ): Promise<runtime.ApiResponse<Array<DashboardWidget>>> {
    if (
      requestParameters.project === null ||
      requestParameters.project === undefined
    ) {
      throw new runtime.RequiredError(
        "project",
        "Required parameter requestParameters.project was null or undefined when calling getDashboardWidgets."
      );
    }

    const queryParameters: runtime.HTTPQuery = {};

    if (requestParameters.project !== undefined) {
      queryParameters["project"] = requestParameters.project;
    }

    if (requestParameters.sort !== undefined) {
      queryParameters["_sort"] = requestParameters.sort;
    }

    const headerParameters: runtime.HTTPHeaders = {};

    if (this.configuration && this.configuration.apiKey) {
      headerParameters["Authorization"] =
        this.configuration.apiKey("Authorization"); // bearerAuth authentication
    }

    const response = await this.request({
      path: `/dashboardwidgets`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters,
    });

    return new runtime.JSONApiResponse(response, (jsonValue) =>
      jsonValue.map(DashboardWidgetFromJSON)
    );
  }

  /**
   * Get list of report templates pinned to a project's dashboard
   * Get list of pinned dashboard widgets
   */
  async getDashboardWidgets(
    requestParameters: GetDashboardWidgetsRequest
  ): Promise<Array<DashboardWidget>> {
    const response = await this.getDashboardWidgetsRaw(requestParameters);
    return await response.value();
  }

  /**
   * Update placement, size, and visualization type for pinned dashboard report widgets
   * Set layout and display settings of dashboard widgets
   */
  async setDashboardWidgetLayoutRaw(
    requestParameters: SetDashboardWidgetLayoutRequest
  ): Promise<runtime.ApiResponse<any>> {
    const queryParameters: runtime.HTTPQuery = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters["Content-Type"] = "application/json";

    if (this.configuration && this.configuration.apiKey) {
      headerParameters["Authorization"] =
        this.configuration.apiKey("Authorization"); // bearerAuth authentication
    }

    const response = await this.request({
      path: `/dashboardwidgets/setLayout`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: DashboardWidgetLayoutPayloadToJSON(
        requestParameters.dashboardWidgetLayoutPayload
      ),
    });

    return new runtime.TextApiResponse(response);
  }

  /**
   * Update placement, size, and visualization type for pinned dashboard report widgets
   * Set layout and display settings of dashboard widgets
   */
  async setDashboardWidgetLayout(
    requestParameters: SetDashboardWidgetLayoutRequest
  ): Promise<any> {
    const response = await this.setDashboardWidgetLayoutRaw(requestParameters);
    return await response.value();
  }

  /**
   * Reorder pinned dashboard widgets
   * Set display order of dashboard widgets
   */
  async setDashboardWidgetSortOrderRaw(
    requestParameters: SetDashboardWidgetSortOrderRequest
  ): Promise<runtime.ApiResponse<any>> {
    const queryParameters: runtime.HTTPQuery = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters["Content-Type"] = "application/json";

    if (this.configuration && this.configuration.apiKey) {
      headerParameters["Authorization"] =
        this.configuration.apiKey("Authorization"); // bearerAuth authentication
    }

    const response = await this.request({
      path: `/dashboardwidgets/setSortOrder`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: DashboardWidgetSortOrderPayloadToJSON(
        requestParameters.dashboardWidgetSortOrderPayload
      ),
    });

    return new runtime.TextApiResponse(response);
  }

  /**
   * Reorder pinned dashboard widgets
   * Set display order of dashboard widgets
   */
  async setDashboardWidgetSortOrder(
    requestParameters: SetDashboardWidgetSortOrderRequest
  ): Promise<any> {
    const response = await this.setDashboardWidgetSortOrderRaw(
      requestParameters
    );
    return await response.value();
  }
}
